// External Dependencies
import type { InvoiceShowResponse } from '@presto-assistant/api_types/api/admin/invoices';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import ReceiptIcon from '@mui/icons-material/Receipt';

// Internal Dependencies
import {
  DetailCard, EnhancedLink, ShowPageDataDisplay,
} from 'components/shared';
import { useGetDistrictById } from 'gql/queries/districts';

// Local Dependencies
import InvoicePaymentForm from './InvoicePaymentForm';

// Local Typings
interface InvoiceInfoContentProps {
  data: InvoiceShowResponse;
}

// Component Definition
const InvoiceInfoContent = ({
  data,
}: InvoiceInfoContentProps): JSX.Element => {
  const {
    estimate,
    invoice,
    invoicePayment,
    status,
    totalInCents,
  } = data;

  const navigate = useNavigate();

  const {
    data: districtData,
  } = useGetDistrictById(invoice.districtId);

  const navigateToDistrictShowPage = useCallback(() => {
    if (invoice.districtId) {
      navigate(`/admin_portals/${invoice.districtId}`);
    }
  }, [navigate, districtData]);

  const navigateToEstimateShowPage = useCallback(() => {
    if (estimate) {
      navigate(`/estimates/${estimate.id}`);
    }
  }, [navigate, estimate]);

  return (
    <>
      <DetailCard
        avatarAriaLabel="Invoice info"
        avatarIcon={<ReceiptIcon />}
        content={(
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
            sx={{ gapX: 3, gapY: 2 }}
          >
            <ShowPageDataDisplay
              label="Invoice Number"
              value={invoice.invoiceNumber}
            />

            <ShowPageDataDisplay
              label="Invoice Date"
              type="date"
              value={invoice.createdAt.toString()}
            />

            <ShowPageDataDisplay
              label="Admin Portal"
              value={(
                <EnhancedLink onClick={navigateToDistrictShowPage}>
                  {districtData?.districtById.label}
                </EnhancedLink>
              )}
            />

            {estimate && (
              <ShowPageDataDisplay
                label="Associated Estimate"
                value={(
                  <EnhancedLink onClick={navigateToEstimateShowPage}>
                    {estimate.estimateNumber}
                  </EnhancedLink>
                )}
              />
            )}

            <ShowPageDataDisplay
              label="Total"
              type="currency"
              value={totalInCents}
            />

            <ShowPageDataDisplay
              label="Status"
              value={status}
            />
          </Box>
        )}
        subheader={invoice.invoiceNumber.toString()}
        title="Invoice"
      />

      <InvoicePaymentForm
        invoiceId={invoice.id}
        invoicePayment={invoicePayment}
      />
    </>
  );
};

export default InvoiceInfoContent;
